import { useEffect, useState } from 'react';
import type { AppAuthApi } from './App.js';
import type { DigestChapter, PublicReport } from './auth/auth-api.js';

/**
 * Страница пересказа по секретной ссылке (/r/<токен>). Открывается без Telegram и без входа —
 * ссылка сама по себе ключ. Поэтому компонент живёт отдельно от дашборда и ничего не знает
 * о сессии: он только читает публичный эндпоинт.
 */

/** Токен из адреса вида /r/<32 hex>; null, если это не страница пересказа. */
export function publicReportToken(pathname: string): string | null {
  const match = pathname.replace(/\/+$/, '').match(/^\/r\/([0-9a-f]{32})$/);
  return match ? match[1]! : null;
}

/** «1:02:03» или «10:03» — час показываем, только если он есть. */
export function timecode(totalMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(totalMs / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** Длительность ролика словами: «1 ч 8 мин» / «14 мин». */
export function durationLabel(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.round((seconds % 3_600) / 60);
  return hours > 0 ? `${hours} ч ${minutes} мин` : `${Math.max(1, minutes)} мин`;
}

/** Ссылка на ролик с нужной секунды — по ней открывается место главы. */
export function chapterLink(youtubeVideoId: string, startMs: number): string {
  return `https://youtu.be/${youtubeVideoId}?t=${Math.floor(startMs / 1_000)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/** Заглушка на месте фрагмента, который модель разбирать отказалась (её ставит бэкенд). */
const BLOCKED_TITLE = 'Фрагмент не разобран';

function ChapterRow({ chapter, videoId }: { chapter: DigestChapter; videoId: string }) {
  const blocked = chapter.title === BLOCKED_TITLE;

  return (
    <li className={`chapter${blocked ? ' chapter--blocked' : ''}`}>
      <a
        className="chapter__time"
        href={chapterLink(videoId, chapter.startMs)}
        target="_blank"
        rel="noreferrer"
      >
        {timecode(chapter.startMs)}
      </a>
      <div className="chapter__body">
        <p className="chapter__title">
          {chapter.highlight && (
            <span className="chapter__star" aria-label="ключевой момент">
              ★
            </span>
          )}
          {chapter.title}
        </p>
        {chapter.bullets.length > 0 && (
          <ul className="chapter__bullets">
            {chapter.bullets.map((bullet, index) => (
              <li key={index}>{bullet}</li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function ReportBody({ report }: { report: PublicReport }) {
  const { video, summary, chapters, claims } = report;
  const duration = durationLabel(video.durationSeconds);
  const keyPoints = summary?.keyPoints ?? [];
  const keyTopics = summary?.keyTopics ?? [];

  return (
    <article className="report">
      <div className="report__cover">
        {video.thumbnailUrl ? (
          <img className="report__thumb" src={video.thumbnailUrl} alt="" />
        ) : (
          <div className="report__thumb report__thumb--empty" aria-hidden="true">
            ▶
          </div>
        )}
        {duration && <span className="report__duration">{duration}</span>}
      </div>

      <h1 className="report__title">{video.title ?? 'Видео'}</h1>
      <p className="report__meta">
        {video.channelName && <span>📺 {video.channelName}</span>}
        <span>разобрано {formatDate(report.createdAt)}</span>
      </p>

      <div className="report__actions">
        <a className="button" href={video.canonicalUrl} target="_blank" rel="noreferrer">
          ▶ Смотреть на YouTube
        </a>
      </div>

      {summary?.overview && (
        <section className="report__block">
          <p className="eyebrow">Кратко</p>
          <p className="report__overview">{summary.overview}</p>
          {keyPoints.length > 0 && (
            <ul className="report__points">
              {keyPoints.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          )}
          {keyTopics.length > 0 && (
            <div className="detail__chips">
              {keyTopics.map((topic, index) => (
                <span key={index} className="chip">
                  {topic}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {chapters.length > 0 && (
        <section className="report__block">
          <p className="eyebrow">
            Подробно по главам <span className="eyebrow__note">{chapters.length} · ★ — ключевые</span>
          </p>
          <ul className="chapters">
            {chapters.map((chapter) => (
              <ChapterRow
                key={chapter.order}
                chapter={chapter}
                videoId={video.youtubeVideoId}
              />
            ))}
          </ul>
        </section>
      )}

      {claims.length > 0 && (
        <section className="report__block">
          <p className="eyebrow">
            Что можно проверить <span className="eyebrow__note">{claims.length}</span>
          </p>
          <ul className="claims">
            {claims.map((claim) => (
              <li key={claim.order} className="claim">
                <span className="claim__text">{claim.text}</span>
                <span className="claim__meta">
                  {claim.category && <span className="claim__tag">{claim.category}</span>}
                  <span className="claim__risk">
                    важность {claim.importance} · риск {claim.riskLevel}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="report__disclaimer">
        Разобрано автоматически, ошибки возможны — сверяйтесь с роликом.
      </p>
    </article>
  );
}

type ReportState =
  | { status: 'loading' }
  | { status: 'ready'; report: PublicReport }
  | { status: 'missing' }
  | { status: 'error'; message: string };

export function PublicReportPage({ api, token }: { api: AppAuthApi; token: string }) {
  const [state, setState] = useState<ReportState>({ status: 'loading' });

  useEffect(() => {
    let active = true;

    api
      .getPublicReport(token)
      .then((report) => {
        if (active) setState({ status: 'ready', report });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Не удалось открыть пересказ';
        // 404 приходит и на чужой токен, и на неготовый разбор — читателю разница не важна.
        setState(/не найден/i.test(message) ? { status: 'missing' } : { status: 'error', message });
      });

    return () => {
      active = false;
    };
  }, [api, token]);

  return (
    <main className="shell shell--report">
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />

      <header className="brand">
        <span className="brand__mark" aria-hidden="true">
          S
        </span>
        <span>
          <strong>STORMVIEW</strong>
          <small>пересказ видео</small>
        </span>
      </header>

      {state.status === 'loading' && (
        <section className="state-card state-card--center" role="status">
          <span className="loader" aria-hidden="true" />
          <p>Открываем пересказ…</p>
        </section>
      )}

      {state.status === 'ready' && <ReportBody report={state.report} />}

      {state.status === 'missing' && (
        <section className="state-card state-card--center">
          <span className="state-card__icon" aria-hidden="true">
            ?
          </span>
          <h1>Пересказ не найден</h1>
          <p>Ссылка неверна или разбор ещё не готов. Попробуйте открыть её позже.</p>
        </section>
      )}

      {state.status === 'error' && (
        <section className="state-card state-card--error" role="alert">
          <h1>Не удалось открыть пересказ</h1>
          <p>{state.message}</p>
        </section>
      )}

      <footer>Проверяйте важное. Не доверяйте громкому.</footer>
    </main>
  );
}
